import { useTheme } from '@material-ui/core/styles'
import React, { FC, useEffect, useRef } from 'react'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@material-ui/core'
import useMediaQuery from '@material-ui/core/useMediaQuery'

import { PrivacyNotice } from './PrivacyNotice'
import { TermsOfSale } from './TermsOfSale'
import { TermsOfUse } from './TermsOfUse'
import { TermsProps } from './types'

const termsModalTitle = ({
  currentlyOpenTermsModal,
}: {
  currentlyOpenTermsModal: TermsProps['currentlyOpenTermsModal']
}) => {
  switch (currentlyOpenTermsModal) {
    case 'privacyNotice':
      return 'PRIVACY NOTICE'
    case 'termsOfSale':
      return 'TERMS OF SALE'
    case 'termsOfUse':
    default:
      return 'TERMS OF USE'
  }
}

export const TermsModal: FC<TermsProps> = ({
  open,
  currentlyOpenTermsModal,
  setCurrentlyOpenTermsModal,
}) => {
  const descriptionElementRef = useRef<HTMLDivElement | null>(null)
  const theme = useTheme()
  const fullScreen = useMediaQuery(theme.breakpoints.down('xs'))

  useEffect(() => {
    const handleAnchorClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement
      if (target.tagName === 'A' && target.getAttribute('href')?.startsWith('#')) {
        event.preventDefault()
        const anchorId = target.getAttribute('href')?.substring(1)
        const anchorElement = anchorId ? document.getElementById(anchorId) : null
        if (anchorElement) {
          anchorElement.scrollIntoView({ behavior: 'smooth' })
        }
      }
    }

    document.addEventListener('click', handleAnchorClick)

    return () => {
      document.removeEventListener('click', handleAnchorClick)
    }
  }, [])

  useEffect(() => {
    if (open) {
      const { current: descriptionElement } = descriptionElementRef
      if (descriptionElement !== null) {
        descriptionElement.focus()
      }
    }
  }, [open, currentlyOpenTermsModal])

  const closeModal = () => setCurrentlyOpenTermsModal(null)

  return (
    <div>
      <Dialog
        aria-describedby="scroll-dialog-description"
        aria-labelledby="scroll-dialog-title"
        fullScreen={fullScreen}
        fullWidth
        maxWidth="md"
        onClose={closeModal}
        open={open}
        scroll="paper"
      >
        <DialogTitle id="scroll-dialog-title">
          {termsModalTitle({ currentlyOpenTermsModal })}
        </DialogTitle>
        <DialogContent dividers>
          <DialogContentText
            component="section"
            id="scroll-dialog-description"
            ref={descriptionElementRef}
            tabIndex={-1}
          >
            <PrivacyNotice
              open={currentlyOpenTermsModal === 'privacyNotice'}
              setCurrentlyOpenTermsModal={setCurrentlyOpenTermsModal}
            />
            <TermsOfSale
              currentlyOpenTermsModal={currentlyOpenTermsModal}
              open={currentlyOpenTermsModal === 'termsOfSale'}
              setCurrentlyOpenTermsModal={setCurrentlyOpenTermsModal}
            />
            <TermsOfUse
              currentlyOpenTermsModal={currentlyOpenTermsModal}
              open={currentlyOpenTermsModal === 'termsOfUse'}
              setCurrentlyOpenTermsModal={setCurrentlyOpenTermsModal}
            />
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeModal} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}

TermsModal.defaultProps = {
  currentlyOpenTermsModal: null,
}
